import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { searchCustomersForSelect } from '@/lib/supabaseDb';
import { useDebounce } from '@/hooks/useDebounce';

function formatTRY(amount: number): string {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(amount);
}

export interface CustomerSearchSelectProps {
  value: string;
  onChange: (customerId: string, customer: any) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  className?: string;
  zIndex?: number;
}

export default function CustomerSearchSelect({
  value,
  onChange,
  placeholder = 'Müşteri ara...',
  label,
  required,
  error,
  disabled,
  className = '',
  zIndex = 50,
}: CustomerSearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [customers, setCustomers] = useState<any[]>([]);
  const [allCustomers, setAllCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const initialLoadDone = useRef(false);

  const debouncedSearch = useDebounce(search, 300);

  const selectedCustomer = useMemo(
    () => customers.find(c => c.id === value) || null,
    [customers, value]
  );

  // Initial load when dropdown opens
  useEffect(() => {
    if (!open || initialLoadDone.current) return;
    initialLoadDone.current = true;
    setLoading(true);
    setLoadError(null);
    searchCustomersForSelect('').then(({ data, error: err }) => {
      if (err) {
        console.error('[CustomerSearchSelect] İlk yükleme hatası:', err);
        setLoadError(err);
        setCustomers([]);
        setAllCustomers([]);
      } else {
        setCustomers(data || []);
        setAllCustomers(data || []);
        setLoadError(null);
      }
      setLoading(false);
    }).catch((e) => {
      console.error('[CustomerSearchSelect] İlk yükleme exception:', e);
      setLoadError('Müşteri listesi yüklenemedi');
      setLoading(false);
    });
  }, [open]);

  // Server-side search when typing (debounced)
  useEffect(() => {
    if (!open || !initialLoadDone.current) return;

    const q = debouncedSearch?.trim() || '';

    if (!q) {
      // Empty search → show all customers
      setCustomers(allCustomers);
      setLoadError(null);
      return;
    }

    if (q.length < 2) {
      // Too short → still show all for now (server handles short queries too)
      // But we won't search server-side yet
      return;
    }

    setLoading(true);
    let cancelled = false;
    searchCustomersForSelect(q).then(({ data, error: err }) => {
      if (cancelled) return;
      if (err) {
        console.error('[CustomerSearchSelect] Arama hatası:', err);
        setLoadError(err);
        setCustomers([]);
      } else {
        setCustomers(data || []);
        setLoadError(null);
      }
      setLoading(false);
    }).catch((e) => {
      if (cancelled) return;
      console.error('[CustomerSearchSelect] Arama exception:', e);
      setLoadError('Arama sırasında hata oluştu');
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [debouncedSearch, open, allCustomers]);

  // Reset state when dropdown closes
  useEffect(() => {
    if (!open) {
      initialLoadDone.current = false;
      setSearch('');
      setLoadError(null);
    }
  }, [open]);

  // Focus input when opening
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Reset highlight on search change
  useEffect(() => {
    setHighlightIndex(0);
  }, [search]);

  const handleSelect = useCallback((customer: any) => {
    onChange(customer.id, customer);
    setOpen(false);
  }, [onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(prev => Math.min(prev + 1, customers.length - 1));
      setTimeout(() => {
        const el = panelRef.current?.querySelector(`[data-idx="${highlightIndex + 1}"]`);
        el?.scrollIntoView({ block: 'nearest' });
      }, 10);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(prev => Math.max(prev - 1, 0));
      setTimeout(() => {
        const el = panelRef.current?.querySelector(`[data-idx="${highlightIndex - 1}"]`);
        el?.scrollIntoView({ block: 'nearest' });
      }, 10);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (customers[highlightIndex]) {
        handleSelect(customers[highlightIndex]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }, [open, customers, highlightIndex, handleSelect]);

  const statusBadge = (customer: any) => {
    const debt = customer.total_debt_amount || 0;
    if (customer.status === 'İcrada') return { label: 'İcrada', cls: 'bg-red-100 text-red-700' };
    if (debt <= 0) return { label: 'Borcu Yok', cls: 'bg-emerald-100 text-emerald-700' };
    return { label: 'Borçlu', cls: 'bg-amber-100 text-amber-700' };
  };

  const inputClass = `w-full px-3 py-2.5 text-sm sm:text-base bg-background-50 border rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-800 placeholder:text-foreground-300 cursor-pointer ${error ? 'border-red-400' : 'border-background-200'}`;

  const labelClass = 'text-xs font-medium text-foreground-500 mb-1.5 block';

  // ─── Panel Content ───
  const panelContent = (
    <div className="flex flex-col" ref={panelRef}>
      {/* Search box + loading indicator */}
      <div className="px-3 py-3 border-b border-background-100">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ad, telefon, müşteri no, TC kimlik..."
            className="w-full pl-9 pr-10 py-2 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-800 placeholder:text-foreground-300"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {loading && (
              <div className="w-4 h-4 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin"></div>
            )}
            {search && (
              <button
                onClick={() => setSearch('')}
                className="w-5 h-5 rounded-full bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300"
              >
                <i className="ri-close-line text-[10px] text-foreground-400"></i>
              </button>
            )}
          </div>
        </div>
        {search.length > 0 && search.length < 2 && (
          <p className="text-xs text-foreground-400 mt-1.5">En az 2 karakter giriniz</p>
        )}
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto overscroll-contain">
        {loadError && customers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center px-4">
            <span className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-3">
              <i className="ri-error-warning-line text-red-400 text-xl"></i>
            </span>
            <p className="text-sm text-foreground-500 font-medium">Yükleme başarısız</p>
            <p className="text-xs text-foreground-400 mt-1">{loadError}</p>
            <button
              onClick={() => {
                setLoadError(null);
                initialLoadDone.current = false;
                // Trigger re-load
                setOpen(false);
                setTimeout(() => setOpen(true), 100);
              }}
              className="mt-3 px-4 py-1.5 text-xs bg-background-100 hover:bg-background-200 rounded-lg cursor-pointer whitespace-nowrap text-foreground-600"
            >
              <i className="ri-refresh-line mr-1"></i>Tekrar Dene
            </button>
          </div>
        ) : search.length >= 2 && customers.length === 0 && !loading ? (
          <div className="flex flex-col items-center justify-center py-12 text-center px-4">
            <span className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mb-3">
              <i className="ri-user-search-line text-foreground-400 text-xl"></i>
            </span>
            <p className="text-sm text-foreground-500 font-medium">Sonuç bulunamadı</p>
            <p className="text-xs text-foreground-400 mt-1">Farklı bir arama terimi deneyin</p>
          </div>
        ) : customers.length > 0 ? (
          customers.map((c, idx) => {
            const badge = statusBadge(c);
            const isHighlighted = idx === highlightIndex;
            const debt = c.total_debt_amount || 0;
            return (
              <div
                key={c.id}
                data-idx={idx}
                onClick={() => handleSelect(c)}
                className={`px-4 py-3 border-b border-background-50 cursor-pointer transition-colors ${isHighlighted ? 'bg-primary-50' : 'hover:bg-background-50'}`}
                onMouseEnter={() => setHighlightIndex(idx)}
              >
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium text-foreground-800">{c.full_name}</p>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${badge.cls}`}>{badge.label}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-foreground-400">
                  <span>{c.phone}</span>
                  {c.customer_no && (
                    <>
                      <span className="text-foreground-200">·</span>
                      <span>{c.customer_no}</span>
                    </>
                  )}
                </div>
                <p className="text-xs mt-1">
                  <span className="text-foreground-400">Kalan Borç: </span>
                  <span className={`font-semibold ${debt > 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                    {formatTRY(debt)}
                  </span>
                </p>
              </div>
            );
          })
        ) : search.length < 2 && !loading ? (
          <div className="flex flex-col items-center justify-center py-12 text-center px-4">
            <span className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mb-3">
              <i className="ri-search-line text-foreground-400 text-xl"></i>
            </span>
            <p className="text-sm text-foreground-500 font-medium">Müşteri arayın</p>
            <p className="text-xs text-foreground-400 mt-1">İsim, telefon, müşteri no veya TC kimlik ile</p>
          </div>
        ) : null}
      </div>
    </div>
  );

  // ─── Render ───
  return (
    <div className={className}>
      {label && (
        <label className={labelClass}>
          {label}
          {required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
      )}

      <div ref={containerRef} className="relative">
        {/* Trigger input */}
        <button
          type="button"
          onClick={() => { if (!disabled) setOpen(!open); }}
          className={`${inputClass} text-left flex items-center justify-between ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={disabled}
        >
          {selectedCustomer ? (
            <span className="text-foreground-800">{selectedCustomer.full_name} — {selectedCustomer.phone}</span>
          ) : (
            <span className="text-foreground-300">{placeholder}</span>
          )}
          <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform ${open ? 'rotate-180' : ''}`}></i>
        </button>

        {selectedCustomer && !disabled && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange('', null); }}
            className="absolute right-8 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-background-200 flex items-center justify-center cursor-pointer hover:bg-red-100 hover:text-red-500 transition-colors"
          >
            <i className="ri-close-line text-[10px]"></i>
          </button>
        )}

        {error && <p className="text-xs text-red-500 mt-1">{error}</p>}

        {/* Desktop dropdown panel */}
        {open && (
          <div
            className="absolute top-full left-0 right-0 mt-1 bg-background-50 border border-background-200 rounded-xl shadow-lg overflow-hidden"
            style={{ zIndex, maxHeight: '380px', minHeight: '200px' }}
          >
            {panelContent}
          </div>
        )}
      </div>

      {/* Mobile bottom sheet */}
      {open && (
        <div className="md:hidden fixed inset-0" style={{ zIndex: zIndex + 10 }}>
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
          ></div>
          {/* Sheet */}
          <div className="absolute bottom-0 left-0 right-0 bg-background-50 rounded-t-2xl max-h-[80vh] flex flex-col shadow-xl animate-slide-up">
            {/* Handle */}
            <div className="flex items-center justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-background-300 rounded-full"></div>
            </div>
            {/* Header */}
            <div className="flex items-center justify-between px-4 pb-3 border-b border-background-100">
              <h3 className="text-base font-semibold text-foreground-900">Müşteri Seç</h3>
              <button
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>
            {/* Content */}
            <div className="flex-1 overflow-hidden" style={{ maxHeight: 'calc(80vh - 70px)' }}>
              {panelContent}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}